import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { FxAlert, FxSegmentedTabs, FxSpinner } from '@/components/fx'
import { PageTitle } from '@/layouts/shellChrome'
import { AnalysisSection } from './AnalysisSection'
import { ApiAccessSection } from './ApiAccessSection'
import { ProfileSection } from './ProfileSection'
import { SecuritySection } from './SecuritySection'
import { VerwaltungSection } from './VerwaltungSection'
import {
  SETTINGS_TAB_WIDTH,
  settingsTabFromSegment,
  settingsTabLabel,
  settingsTabPath,
  settingsTabRequiresAdmin,
  visibleSettingsTabs,
  type SettingsTab,
} from './settingsTabs'
import { useIsAdmin } from './useIsAdmin'

/**
 * `/einstellungen`, with its tabs in the URL (see `settingsTabs.ts`).
 *
 * It renders inside `AppShell`, which supplies the page gutter, the utility
 * row and the `<h1>`'s placement — so this is the tabs and the sections and
 * nothing around them. It claims neither the shell's search field nor its
 * help button: there is nothing here to search, and no help system to open.
 *
 * "Verwaltung" is admin-only twice over: it is absent from the strip for
 * everyone else, *and* its address is refused. Hiding a tab is presentation;
 * the gateway is the real boundary, and it checks the role on every call this
 * panel makes. What the refusal adds is honesty — someone who types the path
 * or follows a colleague's link is told they have no access instead of being
 * shown an empty panel that looks broken.
 */
export function SettingsPage() {
  const { tab: segment } = useParams()
  const navigate = useNavigate()
  const { isAdmin, loading } = useIsAdmin()
  const tab = settingsTabFromSegment(segment)

  // An address that names no tab is a typo or a stale bookmark. Redirecting
  // beats rendering the profile under a URL that says something else.
  if (tab === null) return <Navigate to={settingsTabPath('profil')} replace />

  const refused = settingsTabRequiresAdmin(tab) && !isAdmin

  return (
    <div className="flex flex-col gap-10">
      <PageTitle>Einstellungen</PageTitle>

      <FxSegmentedTabs
        aria-label="Bereiche der Einstellungen"
        items={visibleSettingsTabs(isAdmin).map(({ value, label }) => ({ value, label }))}
        active={tab}
        tabWidth={SETTINGS_TAB_WIDTH}
        onSelect={(value) => navigate(settingsTabPath(value as SettingsTab))}
      />

      {/* `FxSegmentedTabs` does not expose the ids an `aria-controls` pair
          would need, so the panel is named after the tab instead. */}
      <div role="tabpanel" aria-label={settingsTabLabel(tab)} tabIndex={-1}>
        {loading && refused ? (
          // The session is still being restored, so the role is not yet known.
          // "Noch nicht bekannt" is not "no" — an administrator reloading this
          // address must not be shown a refusal that then flips.
          <FxSpinner label="Einstellungen werden geladen" />
        ) : refused ? (
          <FxAlert tone="danger" title="Sie haben keinen Zugriff">
            <p>
              Die Verwaltung ist Administrator:innen vorbehalten. Wenn Sie hier etwas brauchen,
              wenden Sie sich bitte an die Administration Ihrer Organisation.
            </p>
          </FxAlert>
        ) : tab === 'profil' ? (
          <div className="flex flex-col gap-10">
            <ProfileSection />
            <hr className="border-0 border-t-2 border-divider" />
            <SecuritySection />
          </div>
        ) : tab === 'auswertung' ? (
          <AnalysisSection />
        ) : tab === 'apiZugang' ? (
          <ApiAccessSection />
        ) : (
          <VerwaltungSection />
        )}
      </div>
    </div>
  )
}
